import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { type MetricTileData } from './MetricTile';
import { TILE_MIN_HEIGHT } from './TileSkeleton';

/**
 * GhostTile — the single dashed "not enough data yet" placeholder shown at the
 * END of the metrics page (never on the home strip). Metrics that don't yet have
 * enough visits aren't rendered as cards; their names collect here so an owner
 * can see what's coming, with a per-metric progress count toward readiness.
 */

export type GhostMetric = { name: string; applicable?: number; threshold?: number };

/** Pull the ghost-list entries out of a mixed tile array (gated ones only). */
export const toGhostMetrics = (tiles: MetricTileData[]): GhostMetric[] =>
  tiles
    .filter(t => !!t.gated && t.gated.applicable < t.gated.threshold)
    .map(t => ({
      name: t.name,
      applicable: t.gated?.applicable,
      threshold: t.gated?.threshold,
    }));

export const GhostTile: React.FC<{ metrics: GhostMetric[] }> = ({ metrics }) => {
  if (metrics.length === 0) return null;

  return (
    <Box
      sx={{
        border: '1.5px dashed',
        borderColor: 'grey.300',
        borderRadius: 2.5,
        bgcolor: t => alpha(t.palette.grey[500], 0.03),
        p: 1.875,
        minWidth: 260,
        minHeight: TILE_MIN_HEIGHT,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'text.disabled',
        }}
      >
        Not enough data yet
      </Typography>
      <Typography
        sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5, mb: 1.25, lineHeight: 1.4 }}
      >
        {metrics.length} {metrics.length === 1 ? 'metric needs' : 'metrics need'} a few more
        visits before the numbers are reliable. They&apos;ll appear here automatically once
        there&apos;s enough.
      </Typography>

      <Stack spacing={0.625} sx={{ mb: 1.5 }}>
        {metrics.map(m => (
          <Stack
            key={m.name}
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
              {m.name}
            </Typography>
            {m.applicable != null && m.threshold != null && (
              <Typography
                sx={{
                  fontSize: 11,
                  color: 'text.disabled',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {m.applicable} / {m.threshold}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
